import { useRef, useCallback, KeyboardEvent } from 'react'

export interface ChatInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  disabled?: boolean
  placeholder?: string
  className?: string
}

const MIN_ROWS = 1
const MAX_ROWS = 8

export function ChatInput({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Escribe un mensaje...',
  className = '',
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || disabled) return
    onSend()
  }, [value, onSend, disabled])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value)
      const el = e.target
      el.style.height = 'auto'
      el.style.height = `${Math.min(
        Math.max(el.scrollHeight, 24 * MIN_ROWS),
        24 * MAX_ROWS
      )}px`
    },
    [onChange]
  )

  return (
    <div
      className={`border-t border-slate-200 bg-white p-4 ${className}`}
      role="form"
      aria-label="Enviar mensaje"
    >
      <div className="flex gap-2 items-end rounded-xl border border-slate-200 bg-slate-50 focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500 transition-shadow">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          rows={MIN_ROWS}
          className="flex-1 min-h-[44px] max-h-[192px] resize-none bg-transparent px-4 py-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none"
          aria-label="Texto del mensaje"
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={disabled || !value.trim()}
          className="flex-shrink-0 p-2.5 rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Enviar"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-2">
        Enter para enviar, Shift+Enter para nueva línea.
      </p>
    </div>
  )
}
